/**
 * Thin fetch wrapper for the backend.
 *
 * Everything goes through the API. The frontend never talks to Supabase
 * directly, so authorisation, provenance and the review queue stay in one
 * place on the backend.
 *
 * A small read cache sits in here too. The backend sends ETag and
 * Cache-Control, so the browser revalidates a read cheaply and skips the
 * vocabularies outright; this is the layer in front of that, for the span of a
 * few seconds. Two callers asking for the same list at once share one request,
 * and going back to a screen reuses the last answer rather than asking again. A
 * write clears it, so nothing a person just changed is read back stale.
 */

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status}: ${detail}`);
    this.name = "ApiError";
  }
}

/**
 * Set once Firebase auth is wired up. The backend expects the Firebase ID
 * token as a bearer token and resolves it to an app_user itself.
 */
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  // A different token is a different person, so nothing cached for the last one
  // may ever be handed to this one.
  clearReadCache();
}

// How long a read may be reused without asking again. Short on purpose: long
// enough to cover a re-render or a step back to a screen, too short to show a
// person data that has moved on since they last looked.
const TTL_MS = 15_000;

const inflight = new Map<string, Promise<unknown>>();
const cached = new Map<string, { at: number; value: unknown }>();

/** Drop every cached read. Called on a write and on a change of identity. */
export function clearReadCache(): void {
  inflight.clear();
  cached.clear();
}

async function send<T>(path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  // FormData sets its own Content-Type, with the multipart boundary the
  // browser generates. Forcing application/json on it strips the boundary and
  // the backend reads an empty body, so a file upload has to be left alone.
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail ?? detail;
    } catch {
      // A non-JSON error body is fine, the status is the useful part.
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();

  // A write goes straight out and then empties the read cache, so the next read
  // of anything it may have changed comes back fresh rather than as a stale copy
  // from a moment before the change.
  if (method !== "GET") {
    const result = await send<T>(path, init);
    clearReadCache();
    return result;
  }

  const fresh = cached.get(path);
  if (fresh && Date.now() - fresh.at < TTL_MS) {
    return fresh.value as T;
  }

  // One request per path, however many callers want it at the same moment.
  const already = inflight.get(path);
  if (already) {
    return already as Promise<T>;
  }

  const request = send<T>(path, init)
    .then((value) => {
      cached.set(path, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      inflight.delete(path);
    });
  inflight.set(path, request);
  return request;
}
